import { Injectable } from '@angular/core';
import { NumberingItem, NumberingQuery } from '@skylab-shared/business';
import { BusinessObjectBase, IdentifiableValueConverter, LocalContext, ModelBase, RepositoryBase } from '@skylab-shared/core';
import { FormBase, FormBaseViewModel } from '../form/form-base';
import { NumberingItemWithPreview } from './../form/form-numberings';

export type DocumentFormViewModelNumberingExtension = {
    getNumberingFormatPreview: () => string;
    isModelNumberingItem(item: NumberingItem): boolean;
    isSelectedNumberingManual: () => boolean;
    numberingsConverter: IdentifiableValueConverter;
    numberings: {
        value: NumberingItemWithPreview[];
    };
};

interface DocumentModel extends ModelBase {
    readonly numberingId: number;
}

@Injectable({ providedIn: 'root' })
export class DocumentFormViewModelNumberingExtensionService {

    public extendViewModel<TViewModelPart, TViewModel extends TViewModelPart & FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        viewModel: TViewModelPart,
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext,
        availableNumberings: NumberingItemWithPreview[]): TViewModelPart & DocumentFormViewModelNumberingExtension {
        return Object.assign(viewModel as any, this.getViewModelExtension(formComponent, localContext, availableNumberings));
    }

    private getViewModelExtension<TViewModel extends FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext,
        availableNumberings: NumberingItemWithPreview[]): DocumentFormViewModelNumberingExtension {
        const model = formComponent.bo.model;

        let numberings = model.isPersistent
            ? availableNumberings.filter(x => x.active || x.id == model.numberingId)
            : availableNumberings.filter(x => x.active);

        const viewModelExtension: DocumentFormViewModelNumberingExtension = {
            isSelectedNumberingManual: () => !!localContext.firstPreloaded(NumberingQuery, { id: model.numberingId })?.manual,
            getNumberingFormatPreview: () => viewModelExtension.numberings.value.find(elem => elem.id === model.numberingId)?.formatPreview || '',
            isModelNumberingItem: (item: NumberingItem) => item.id === model.numberingId,
            numberingsConverter: new IdentifiableValueConverter(numberings),
            numberings: {
                get value() {
                    return numberings;
                },
                set value(val) {
                    numberings = val;
                }
            }
        };

        return viewModelExtension;
    }
}
